/**
 * Mail state mutations — star, archive, snooze, assign, move (N1).
 *
 * One hook family for BOTH inboxes. The lane is carried in the target
 * (`inbox_id` present = unified, absent = personal), so nothing here branches
 * on which surface called it — the server resolves the privilege boundary from
 * the same field.
 *
 * Every mutation takes the `target` shape `useMailSelection.buildTarget()`
 * produces: exactly one of `ids` or `predicate`. The predicate form is what
 * makes "Select all 248" work without the browser materialising 248 ids.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { apiPost } from '../lib/api';
import { notifyBellChanged } from '../lib/bellLinkage';
import { formatInZone, naiveInZone } from '../lib/timezone';
import { useAuth } from '../context/AuthContext';
// The rail's query key lives with the rail's hook — one factory, so an
// invalidation here can never drift from the key the rail actually reads.
import { mailFolderKeys } from './useMailFolders';

/**
 * Snooze presets, resolved in the VIEWER's display zone.
 *
 * Computed client-side and sent as an absolute UTC instant — the API accepts
 * no symbolic preset name. Resolving "tomorrow 09:00" server-side would use
 * the stored PROFILE timezone, which is not necessarily the zone the reader is
 * reading in; that divergence is the entire reason #578/#540 exist.
 *
 * `zone` is an IANA name from `useDisplayZone()`. It is a PARAMETER rather
 * than a context read so the function stays pure and its boundary cases (DST,
 * week rollover) are testable without mounting a provider.
 *
 * An earlier version documented that parameter and did not have it: it used
 * `Date.prototype.setHours`, which is the BROWSER's zone. A reader in Sydney
 * looking at a mailbox they have set to display in London got "tomorrow 09:00"
 * meaning 09:00 Sydney — the exact profile-vs-display divergence this comment
 * claims to avoid. The wall-clock target is now built as a naive string and
 * converted through the zone.
 */
export function resolveSnoozePreset(preset, now = new Date(), zone = null) {
    const wallClock = (date, hour) => {
        // A naive `YYYY-MM-DDTHH:mm:ss` in the TARGET zone, which
        // `naiveInZone` then converts to a real instant.
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        const h = String(hour).padStart(2, '0');
        return `${y}-${m}-${d}T${h}:00:00`;
    };

    // Day arithmetic is done on the viewer's calendar, so "tomorrow" means
    // their tomorrow. `zoneToday` is midday in the zone to keep the date
    // stable across the DST hours where a local midnight can be ambiguous.
    const base = zone
        ? new Date(formatInZone(now, { zone, formatStr: "yyyy-MM-dd'T'12:00:00" }))
        : new Date(now);

    const toInstant = (date, hour) => {
        const naive = wallClock(date, hour);
        if (!zone) {
            // No display zone resolved — fall back to the browser's, which is
            // what the reader is looking at anyway.
            const local = new Date(naive);
            return Number.isNaN(local.getTime()) ? null : local;
        }
        const iso = naiveInZone(naive, zone);
        return iso ? new Date(iso) : null;
    };

    switch (preset) {
        case 'later_today': {
            const d = new Date(now);
            d.setHours(d.getHours() + 3, 0, 0, 0);
            return d;
        }
        case 'tomorrow': {
            const d = new Date(base);
            d.setDate(d.getDate() + 1);
            return toInstant(d, 9);
        }
        case 'this_weekend': {
            const d = new Date(base);
            // Saturday === 6. If today IS Saturday, mean NEXT Saturday, not
            // nine hours ago — a preset that resolves into the past is refused
            // by the API, which would read to the user as a broken button.
            const delta = (6 - d.getDay() + 7) % 7 || 7;
            d.setDate(d.getDate() + delta);
            return toInstant(d, 9);
        }
        case 'next_week': {
            const d = new Date(base);
            const delta = (1 - d.getDay() + 7) % 7 || 7;
            d.setDate(d.getDate() + delta);
            return toInstant(d, 9);
        }
        default:
            return null;
    }
}

/**
 * Whether this write moved mail into or out of the bell's view (#803): Delete
 * (`move` to Trash), Restore (`move` back to the Inbox, `folder: null`) and
 * Empty Trash. TopBar refetches on the event; the server has already done the
 * work. A move to Spam or a custom folder never touches the bell.
 */
function changesTheBell(path, variables) {
    if (path === 'trash/empty') return true;
    return path === 'move' && (variables?.folder === 'trash' || variables?.folder == null);
}

function useMailStateMutation(path) {
    const { getAuthHeader } = useAuth();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (body) => apiPost(`/mail/${path}`, body, getAuthHeader()),
        onSuccess: (_data, variables) => {
            const inboxId = variables?.target?.inbox_id ?? null;
            // The rail's counts are cached server-side for 30s and invalidated
            // on write; refetch so the number the user just changed follows
            // immediately rather than lagging a poll.
            queryClient.invalidateQueries({
                queryKey: mailFolderKeys.lane(inboxId),
            });
            // Invalidate the lane the mutation touched, not both.
            //
            // This used to fire `['inbox']` and `['personal-inbox']`
            // unconditionally. Both are PREFIX matches, so one star click on a
            // personal message refetched every loaded personal page, every
            // unified inbox list, every open thread and the mailbox list —
            // including the entire opposite lane, which the mutation cannot
            // have touched. The lane is already known from the target.
            queryClient.invalidateQueries({
                queryKey: inboxId ? ['inbox', inboxId, 'messages'] : ['personal-inbox', 'emails'],
            });
            const threads = { queryKey: inboxId ? ['inbox', inboxId, 'thread'] : ['inbox', 'thread'] };
            // #803 — Empty Trash deletes for good: drop those threads' caches
            // rather than refetch them, so nothing can render the kept copy.
            if (path === 'trash/empty') queryClient.removeQueries(threads);
            else queryClient.invalidateQueries(threads);
            if (changesTheBell(path, variables)) notifyBellChanged();
        },
    });
}

export const useStarMail = () => useMailStateMutation('star');
/**
 * The Important flag. CRM-owned on both providers — Gmail's `IMPORTANT` is
 * Google's inference and would need `gmail.modify` to write, Outlook has no
 * equivalent, so there is deliberately no provider mirror and no scope hint.
 */
export const useImportantMail = () => useMailStateMutation('important');
/**
 * Empty Trash — the one bulk tombstone. Everything else that says "delete"
 * is a move; this is the step that makes it permanent (#692).
 */
export const useEmptyTrash = () => useMailStateMutation('trash/empty');
/** Mark read or unread. The unread direction had no backend until N1. */
export const useReadMail = () => useMailStateMutation('read');
export const useArchiveMail = () => useMailStateMutation('archive');
export const useSnoozeMail = () => useMailStateMutation('snooze');
export const useAssignMail = () => useMailStateMutation('assign');
export const useMoveMail = () => useMailStateMutation('move');
/**
 * Apply / remove org tags on mail (#646). Body `{ target, add, remove,
 * expand_conversations }` with tag_ids; answers `{ requested, matched, added,
 * removed, failed: [{ comm_id, reason }] }`. Same lane invalidation as the
 * rest — the rail's tag counts ride the same cached model.
 */
export const useTagMail = () => useMailStateMutation('tags');
